import { useState } from 'react';

import { Icon } from '../components/Icon.js';
import styles from './NewListRow.module.css';

/**
 * Row scale. `sm` is menu type (the picker, the header pill's menu); `md` is the
 * rail panel's list rows, which are a size up.
 */
type Scale = 'sm' | 'md';

/** The "+ New list" row at rest — the twin of the naming row below. */
export function NewListButton({ scale = 'sm', onClick }: { scale?: Scale; onClick: () => void }) {
  return (
    <button
      type="button"
      className={scale === 'md' ? styles.buttonMd : styles.button}
      onClick={onClick}
    >
      <Icon name="plus" size={15} />
      New list
    </button>
  );
}

interface NewListRowProps {
  /** The word on the confirm: what pressing it does to the chats in hand. */
  confirm: string;
  scale?: Scale;
  onCreate: (name: string) => void;
  onCancel: () => void;
}

/**
 * Naming a new list, in the row the "+ New list" button was using — same box, so
 * the switch moves nothing.
 */
export function NewListRow({ confirm, scale = 'sm', onCreate, onCancel }: NewListRowProps) {
  const [name, setName] = useState('');

  const commit = () => {
    const trimmed = name.trim();
    if (trimmed) onCreate(trimmed);
    setName('');
  };

  return (
    <div className={scale === 'md' ? styles.rowMd : styles.row}>
      <span className={styles.icon}>
        <Icon name="plus" size={15} />
      </span>
      <input
        className={scale === 'md' ? styles.inputMd : styles.input}
        placeholder="List name"
        value={name}
        autoFocus
        onChange={(event) => setName(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') commit();
          if (event.key === 'Escape') {
            // Stopped here so it doesn't reach a dialog's own Escape handler:
            // abandoning a half-typed name must not close the dialog around it.
            event.stopPropagation();
            setName('');
            onCancel();
          }
        }}
      />
      <button type="button" className={styles.confirm} disabled={!name.trim()} onClick={commit}>
        {confirm}
      </button>
    </div>
  );
}
